import { useState, useRef, forwardRef, useCallback, RefObject, useEffect } from 'react';
import DateTime, { registerLocale } from 'react-datepicker';
import cn from 'classnames';
import ru from 'date-fns/locale/ru';
import 'react-datepicker/dist/react-datepicker.css';

import { IconCollapse } from 'UIPackage/IconsModule/components/IconCollapse';
import {
  DATE_FORMAT_FULL_FNS,
  DATE_FORMAT_LONG_MINUTE_FNS,
  TIME_FORMAT,
} from 'CorePackage/MainModule/constants/dateFormats';
import { ErknmFillInProcessMessage } from 'KnmErknmPackage/MainModule/components/ErknmFillInProcessMessage';

import styles from './TimePicker.module.scss';
import { formatDate, parseDate } from '../../../CorePackage/MainModule/common/formatDate';

registerLocale('ru', ru);

interface IDatetimeRef extends DateTime {
  state: { open: string };
  setOpen: (state: boolean) => void;
}

export interface ITimePickerProps {
  name: string;
  className?: string;
  classNameError?: string;
  id?: string;
  value?: string | Date;
  placeholder?: string;
  disabled?: boolean;
  invalid?: boolean;
  opensOnTop?: boolean;
  error?: string;
  minDate?: Date;
  maxDate?: Date;
  onChange?: (date: string | Date) => void;
}

interface ICustomInputProps {
  name: string;
  className?: string;
  id?: string;
  value?: string;
  placeholder: string;
  isOpenedCalendar: boolean;
  handleButtonTogglerClick: () => void;
  handleChange?: (time: string) => void;
  disabledMain?: boolean;
}

interface ICustomTimeInputProps {
  onChange?: (time: string) => void;
  value?: string;
}

const CustomInput = forwardRef(
  (
    {
      id,
      className,
      name,
      placeholder,
      value,
      isOpenedCalendar,
      handleButtonTogglerClick,
      handleChange,
      disabledMain,
    }: ICustomInputProps,
    ref: RefObject<HTMLDivElement>,
  ) => {
    const [time, setTime] = useState(value);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
      setTime(value);
    }, [value]);

    const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const input = e.target.value;

      // Разрешаем только цифры и двоеточие
      const sanitized = input.replace(/[^0-9:]/g, '');

      // Ограничиваем длину
      if (sanitized.length > 5) return;

      // Автоматически добавляем двоеточие после двух цифр
      let newTime = sanitized;

      if ((time?.length === 4 || time?.length === 1) && sanitized.length === 2 && !sanitized.includes(':')) {
        newTime = `${sanitized}:`;
      }

      // Проверяем формат hh:mm
      const timeRegex = /^([0-1]?[0-9]|2[0-3]):?([0-5][0-9])?$/;

      setTime(newTime);

      if (handleChange && (newTime === '' || timeRegex.test(newTime))) {
        handleChange(newTime);
      }
    };

    const handleBlur = () => {
      // Добавляем ведущие нули и форматируем время при потере фокуса
      if (time && time.length > 0) {
        const parts = time.split(':');
        let hours = parts[0] || '';
        let minutes = parts[1] || '';

        // Добавляем ведущий ноль к часам, если нужно
        if (hours.length === 1 && parseInt(hours, 10) < 10) {
          hours = `0${hours}`;
        }

        // Добавляем ведущий ноль к минутам, если нужно
        if (minutes.length === 1 && parseInt(minutes, 10) < 10) {
          minutes = `0${minutes}`;
        }

        // Добавляем ведущий ноль к минутам, если нужно
        if (parseInt(minutes, 10) > 59) {
          minutes = `59`;
        }

        // Формируем новое время
        let formattedTime = hours;

        if (minutes !== '') {
          formattedTime += `:${minutes}`;
        }

        // Проверяем валидность часов (0-23)
        if (parseInt(hours, 10) > 23) {
          hours = '23';
          formattedTime = minutes !== '' ? `${hours}:${minutes}` : hours;
        }

        setTime(formattedTime);

        if (handleChange) handleChange(formattedTime);
      }
    };

    return (
      <div
        ref={ref}
        onClick={handleButtonTogglerClick}
        className={isOpenedCalendar ? 'react-datepicker-ignore-onclickoutside' : ''}
      >
        <input
          id={id}
          name={name}
          ref={inputRef}
          className={className}
          placeholder={placeholder}
          value={time}
          disabled={disabledMain}
          onChange={onChange}
          onBlur={handleBlur}
        />
      </div>
    );
  },
);

const CustomTimeInput = ({ onChange, value }: ICustomTimeInputProps) => {
  let validTime = '';

  if (value) {
    validTime = value;
  } else {
    validTime = '00:00';
  }

  const splitTime = validTime.split(':');

  function handleChangeTime(type: string, action: string): string {
    if (type === 'hours') {
      const hours = action === '+' ? +splitTime[0] + 1 : +splitTime[0] - 1;

      return `${hours}:${splitTime[1]}`;
    }
    if (type === 'minutes') {
      const minutes = action === '+' ? +splitTime[1] + 1 : +splitTime[1] - 1;

      return `${splitTime[0]}:${minutes}`;
    }

    return '';
  }

  function onHourInc() {
    if (onChange) {
      onChange(handleChangeTime('hours', '+'));
    }
  }

  function onHourDec() {
    if (onChange) {
      onChange(handleChangeTime('hours', '-'));
    }
  }

  function onMinuteInc() {
    if (onChange) {
      onChange(handleChangeTime('minutes', '+'));
    }
  }

  function onMinuteDec() {
    if (onChange) {
      onChange(handleChangeTime('minutes', '-'));
    }
  }

  return (
    <>
      <div className={styles.TimeWrapper}>
        <div className={styles.TimeContainer}>
          <span className={styles.IncreaseTimeBtn} onClick={onHourInc}>
            <IconCollapse className={styles.ChevronIcon} />
          </span>
          <div className={styles.TimeView}>{splitTime[0]}</div>
          <span className={styles.DecreaseTimeBtn} onClick={onHourDec}>
            <IconCollapse className={styles.ChevronIcon} />
          </span>
        </div>
        <div className={styles.TimeSeparator}>:</div>
        <div className={styles.TimeContainer}>
          <span className={styles.IncreaseTimeBtn} onClick={onMinuteInc}>
            <IconCollapse className={styles.ChevronIcon} />
          </span>
          <div className={styles.TimeView}>{splitTime[1]}</div>
          <span className={styles.DecreaseTimeBtn} onClick={onMinuteDec}>
            <IconCollapse className={styles.ChevronIcon} />
          </span>
        </div>
      </div>
    </>
  );
};

export const TimePicker = ({
  className,
  classNameError,
  id,
  name,
  value,
  placeholder = '',
  disabled,
  invalid,
  error,
  opensOnTop,
  minDate = new Date('01-01-1917'),
  maxDate = new Date('12-31-2050'),
  onChange,
}: ITimePickerProps) => {
  const [isRenderTimeView, setIsRenderTimeView] = useState<boolean>(false);
  const datetimeRef = useRef<IDatetimeRef>(null);
  const ref = useRef(null);

  registerLocale('ru', ru);

  const handleButtonTogglerClick = useCallback(() => {
    if (!disabled && datetimeRef.current) {
      setIsRenderTimeView(!isRenderTimeView);

      if (!datetimeRef.current.state.open) {
        datetimeRef.current.setOpen(true);
      } else {
        datetimeRef.current.setOpen(false);
      }
    }
  }, [disabled, isRenderTimeView]);

  const handleCloseCalendar = useCallback(() => {
    setIsRenderTimeView(false);
  }, []);

  const onDateChange = useCallback(
    (date: Date) => {
      if (onChange) {
        onChange(date);
      }
    },
    [onChange],
  );

  let currentMomentValue = formatDate({
    date: value || '',
    dateFormat: DATE_FORMAT_FULL_FNS,
    parseFormat: DATE_FORMAT_FULL_FNS,
  });

  if (!currentMomentValue) {
    currentMomentValue = formatDate({
      date: value || '',
      dateFormat: DATE_FORMAT_FULL_FNS,
      parseFormat: DATE_FORMAT_LONG_MINUTE_FNS,
    });
  }
  const selectedValue = currentMomentValue
    ? parseDate({ date: currentMomentValue, parseFormat: DATE_FORMAT_FULL_FNS })
    : undefined;

  const handleChange = useCallback(
    (time: string) => {
      const currentValue = selectedValue || new Date();
      const currentValueString = formatDate({
        date: currentValue,
        dateFormat: DATE_FORMAT_FULL_FNS,
        parseFormat: DATE_FORMAT_FULL_FNS,
      });
      const currentArray = currentValueString.split(' ');
      const newDateTime = `${currentArray[0]} ${time}:00`;
      const newDate = parseDate({ date: newDateTime, parseFormat: DATE_FORMAT_FULL_FNS });

      if (time.length === 5 && newDate && onChange) onChange(newDate);
    },
    [onChange, selectedValue],
  );

  return (
    <div
      className={cn(
        styles.DatePicker,
        {
          [styles.DatePickerDisabled]: disabled,
          [styles.DatePickerInvalid]: invalid,
          [styles.DatePickerOpensOnTop]: opensOnTop,
        },
        className,
      )}
      ref={ref}
    >
      <DateTime
        ref={datetimeRef}
        className={cn(styles.Input, {
          [styles.InputDisabled]: disabled,
          [styles.InputInvalid]: invalid,
        })}
        placeholderText={placeholder}
        selected={selectedValue}
        onChange={onDateChange}
        onCalendarClose={handleCloseCalendar}
        timeFormat={TIME_FORMAT}
        locale="ru"
        showTimeInput={isRenderTimeView}
        showTimeSelect
        showTimeSelectOnly
        dateFormat={'HH:mm'}
        timeIntervals={1}
        timeCaption="Время"
        customTimeInput={<CustomTimeInput />}
        customInput={
          <CustomInput
            id={id}
            name={name}
            placeholder={placeholder}
            handleButtonTogglerClick={handleButtonTogglerClick}
            isOpenedCalendar={isRenderTimeView}
            handleChange={handleChange}
            disabledMain={disabled}
          />
        }
        minDate={minDate}
        maxDate={maxDate}
      />
      {invalid && error && <div className={cn(styles.DatePickerError, classNameError)}>{error}</div>}
      {!invalid && !error && <ErknmFillInProcessMessage name={name} />}
    </div>
  );
};
